import * as Yup from 'yup';
import { SUPPORTED_LANGUAGES } from '../i18n';

// Helper function to convert multilingual data to old format for backward compatibility
export const convertToBackendFormat = (multiLangData) => {
  // Use English as primary language (fallback)
  const result = {
    details: multiLangData.details?.en || multiLangData.details || '',
    method: multiLangData.method?.en || multiLangData.method || '',
    response: multiLangData.response?.en || multiLangData.response || '',
    preamble: multiLangData.preamble?.en || multiLangData.preamble || '',
    facilitatorNote:
      multiLangData.facilitatorNote?.en || multiLangData.facilitatorNote || '',
    translations: {
      details: multiLangData.details,
      method: multiLangData.method,
      response: multiLangData.response,
      preamble: multiLangData.preamble,
      facilitatorNote: multiLangData.facilitatorNote,
    },
  };

  return result;
};

// Helper function to convert legacy data to multilingual format
export const convertFromBackendFormat = (legacyData) => {
  // If already converted, return as-is
  if (
    legacyData.details &&
    typeof legacyData.details === 'object' &&
    legacyData.details.en !== undefined
  ) {
    return legacyData;
  }

  if (legacyData.translations) {
    return {
      id: legacyData.id, // Preserve the ID for editing
      details: legacyData.translations.details || { en: legacyData.details },
      method: legacyData.translations.method || { en: legacyData.method },
      response: legacyData.translations.response || { en: legacyData.response },
      preamble: legacyData.translations.preamble || { en: legacyData.preamble },
      facilitatorNote: legacyData.translations.facilitatorNote || {
        en: legacyData.facilitatorNote,
      },
    };
  }

  // Legacy format without translations
  return {
    id: legacyData.id, // Preserve the ID for editing
    details: { en: legacyData.details || '' },
    method: { en: legacyData.method || '' },
    response: { en: legacyData.response || '' },
    preamble: { en: legacyData.preamble || '' },
    facilitatorNote: { en: legacyData.facilitatorNote || '' },
  };
};

export const initialValues = (form) => {
  if (!form) {
    // Initialize empty values for all supported languages
    const values = {
      details: {},
      method: {},
      response: {},
      preamble: {},
      facilitatorNote: {},
    };

    SUPPORTED_LANGUAGES.forEach((lang) => {
      values.details[lang.locale] = '';
      values.method[lang.locale] = '';
      values.response[lang.locale] = '';
      values.preamble[lang.locale] = '';
      values.facilitatorNote[lang.locale] = '';
    });

    return values;
  }

  // Convert legacy format to multilingual
  const convertedForm = convertFromBackendFormat(form);

  // Ensure all supported languages have entries
  const result = {
    details: {},
    method: {},
    response: {},
    preamble: {},
    facilitatorNote: {},
  };

  SUPPORTED_LANGUAGES.forEach((lang) => {
    result.details[lang.locale] = convertedForm.details?.[lang.locale] || '';
    result.method[lang.locale] = convertedForm.method?.[lang.locale] || '';
    result.response[lang.locale] = convertedForm.response?.[lang.locale] || '';
    result.preamble[lang.locale] = convertedForm.preamble?.[lang.locale] || '';
    result.facilitatorNote[lang.locale] =
      convertedForm.facilitatorNote?.[lang.locale] || '';
  });

  return result;
};

// Multilingual validation schema
const multiLangStringSchema = (t, required = false) => {
  const languageValidation = {};

  SUPPORTED_LANGUAGES.forEach((lang) => {
    if (lang.locale === 'en' && required) {
      languageValidation[lang.locale] = Yup.string().required(
        t('formulary.validation.english_translation_required')
      );
    } else {
      languageValidation[lang.locale] = Yup.string().optional();
    }
  });

  return Yup.object().shape(languageValidation);
};

export const methodValidation = (t) =>
  Yup.object().shape({
    details: multiLangStringSchema(t, true), // Required field
    method: multiLangStringSchema(t, false), // Optional (read-only)
    response: multiLangStringSchema(t, false), // Optional (read-only)
    preamble: multiLangStringSchema(t, false), // Optional
    facilitatorNote: multiLangStringSchema(t, false), // Optional
  });
